import {useCallback, useEffect, useState} from 'react';

import {addQueryToPath} from '../../libs/shared/helpers/src/browser/urlHelpers';
import {Handler} from '../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../libs/shared/helpers/src/types/asyncStatuses';
import {useUnmountCancelToken} from '../../srcCommon/helpers/hookHelpers';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {ConversationSafetyNetModel} from '../models/api/conversationSafetyNetModel';
import {ApiSafetyNets, SafetyNetModel, SafetyNetsEnum} from '../models/api/safetyNetModel';
import {ApiTagsPath, TagPathModel} from '../models/api/tagModel';
import {fetchApiAsync, postApiAsync} from '../services/http/http';
import {getCompany} from '../store/data/company/companySelectors';
import {useAppSelector} from './reduxHelpers';

/*
 * SafetyNets.
 */

export function isSafetyNetSatisfied(
  safetyNet: ConversationSafetyNetModel,
  safetyNetType: SafetyNetsEnum,
): boolean {
  const {isOptimisticallySatisfied, isSatisfied, type} = safetyNet;

  if (type !== safetyNetType) {
    return true;
  }

  return isOptimisticallySatisfied ?? isSatisfied;
}

/*
 * Required Tagging.
 */

export function useFetchTagList(tagIds: ReadonlyArray<number>): {
  tagsPath?: ReadonlyArray<TagPathModel>;
  status: AsyncStatusesEnum;
  onRetry: Handler;
} {
  const company = useAppSelector(getCompany);

  const [tagsPath, setTagsPath] = useState<ReadonlyArray<TagPathModel>>();
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);
  const cancelToken = useUnmountCancelToken();

  const loadTags = useCallback(async () => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    try {
      const apiTagsPath = await postApiAsync(
        ApiTagsPath,
        `${company.url}/tags_batch`,
        {
          ids: tagIds,
        },
        {cancelToken},
      );
      setTagsPath(apiTagsPath);
      setLoadingStatus(AsyncStatusesEnum.LOADED);
    } catch {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
    }
  }, [cancelToken, company.url, tagIds]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  return {tagsPath, status: loadingStatus, onRetry: loadTags};
}

export function useFetchSafetyNets(safetyNetIds: ReadonlyArray<number>) {
  const company = useAppSelector(getCompany);

  const [safetyNets, setSafetyNets] = useState<ReadonlyArray<SafetyNetModel>>([]);
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);

  const cancelToken = useUnmountCancelToken();

  const loadSafetyNets = useCallback(() => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    const apiUrl = addQueryToPath(`${company.url}/safety_nets`, {ids: safetyNetIds.join(',')});
    fetchApiAsync(ApiSafetyNets, apiUrl, {cancelToken})
      .then((apiSafetyNets) => {
        setSafetyNets(apiSafetyNets);
        setLoadingStatus(AsyncStatusesEnum.LOADED);
      })
      .catch(() => setLoadingStatus(AsyncStatusesEnum.FAILED));
  }, [cancelToken, company.url, safetyNetIds]);

  useEffect(() => {
    loadSafetyNets();
  }, [loadSafetyNets]);

  return {safetyNets, loadingStatus, onRetry: loadSafetyNets};
}

export function areConversationSafetyNetsSatisfied(
  conversation: ConversationBaseModel,
  safetyNetType: SafetyNetsEnum,
) {
  if (!conversation.safetyNets) {
    return true;
  }

  return conversation.safetyNets.every((safetyNet) => isSafetyNetSatisfied(safetyNet, safetyNetType));
}
